/**
 * Shifts Hooks (Phase 9.1)
 *
 * TanStack Query hooks for shift CRUD operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  ShiftWithCount,
  ShiftLookup,
  Shift,
  CreateShift,
  UpdateShift,
  ShiftRotation,
} from '@guild/shared';

// Query keys
export const shiftKeys = {
  all: ['shifts'] as const,
  lists: () => [...shiftKeys.all, 'list'] as const,
  list: (filters: { search?: string; includeInactive?: boolean }) =>
    [...shiftKeys.lists(), filters] as const,
  lookup: () => [...shiftKeys.all, 'lookup'] as const,
  details: () => [...shiftKeys.all, 'detail'] as const,
  detail: (id: string) => [...shiftKeys.details(), id] as const,
  rotation: () => [...shiftKeys.all, 'rotation'] as const,
};

/**
 * Fetch shifts list with operator counts
 */
export function useShifts(options?: { search?: string; includeInactive?: boolean }) {
  const api = useApi();

  return useQuery({
    queryKey: shiftKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.includeInactive) params.set('includeInactive', 'true');

      const queryString = params.toString();
      const endpoint = queryString ? `/api/shifts?${queryString}` : '/api/shifts';

      return api.get<ShiftWithCount[]>(endpoint);
    },
  });
}

/**
 * Fetch lightweight shifts list for dropdowns
 */
export function useShiftLookup() {
  const api = useApi();

  return useQuery({
    queryKey: shiftKeys.lookup(),
    queryFn: () => api.get<ShiftLookup[]>('/api/shifts/lookup'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (rarely changes)
  });
}

/**
 * Fetch single shift
 */
export function useShift(id: string) {
  const api = useApi();

  return useQuery({
    queryKey: shiftKeys.detail(id),
    queryFn: () => api.get<Shift>(`/api/shifts/${id}`),
    enabled: !!id,
  });
}

/**
 * Create shift mutation
 */
export function useCreateShift() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateShift) => api.post<Shift>('/api/shifts', data),
    onSuccess: () => {
      // Invalidate all shift queries
      queryClient.invalidateQueries({ queryKey: shiftKeys.all });
    },
  });
}

/**
 * Update shift mutation
 */
export function useUpdateShift() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateShift }) =>
      api.put<Shift>(`/api/shifts/${id}`, data),
    onSuccess: (_, variables) => {
      // Invalidate list and specific detail
      queryClient.invalidateQueries({ queryKey: shiftKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shiftKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: shiftKeys.lookup() });
    },
  });
}

/**
 * Delete shift mutation (soft delete)
 */
export function useDeleteShift() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/shifts/${id}`),
    onSuccess: () => {
      // Invalidate all shift queries
      queryClient.invalidateQueries({ queryKey: shiftKeys.all });
    },
  });
}

// ============================================
// CREW ROTATION (Feature 2)
// ============================================

/**
 * Fetch the org's crew rotation. `null` means none is set: KPI results are
 * attributed by each shift's hours.
 */
export function useShiftRotation() {
  const api = useApi();

  return useQuery({
    queryKey: shiftKeys.rotation(),
    queryFn: () => api.get<ShiftRotation | null>('/api/shifts/rotation'),
  });
}

/**
 * Save (create or replace) the org's crew rotation (admin only).
 * `onSuccess` returns the invalidation, so `mutateAsync` resolves only once the
 * rotation has been refetched and the panel never shows the old one.
 */
export function useSaveShiftRotation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (rotation: ShiftRotation) =>
      api.put<ShiftRotation>('/api/shifts/rotation', rotation),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shiftKeys.rotation() }),
  });
}

/**
 * Remove the org's crew rotation (admin only). New KPI results are then
 * attributed by each shift's hours again. Resolves after the refetch, as above.
 */
export function useDeleteShiftRotation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.delete<null>('/api/shifts/rotation'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: shiftKeys.rotation() }),
  });
}
